/* eslint-disable no-case-declarations */
import type { ICreatePayApplicationCmd } from "@/data/commands/planning/CreatePayApplicationCmd";
import { Factory } from "@/data/dao/Factory";
import type { DaoError, IDataSource, IPaginatedResult } from "@/data/dao/service/BaseServiceDao";
import { LocationDao } from "@/data/dao/service/LocationDao";
import { PlanningDao } from "@/data/dao/service/PlanningDao";
import { PaymentMethodMode, ServiceType } from "@/data/model/Enums";
import type { IPayApplicationWithPaymentMethods } from "@/data/model/PayApplication";
import type { ILinkableEntity } from "@/data/model/Shared";
import type { IUseCasePaymentMethod } from "@/data/model/UseCasePaymentMethod";



import { translate } from "@/shared/localization/language";



import { SelectBootstrap } from "@/operator/elements/SelectBootstrap";



import { i18n } from "@/portal/Trans";
import { MultiLanguageInput } from "@/portal/formControls/MultiLanguageInput";
import { NumberInput } from "@/portal/formControls/NumberInput";
import { Switch } from "@/portal/formControls/Switch";
import { BaseEntityForm } from "@/portal/forms/BaseEntityForm";
import { TastierGlobal } from "@/portal/globalAccess";


export class PayApplicationForm extends BaseEntityForm<IPayApplicationWithPaymentMethods, ICreatePayApplicationCmd> {
    private originalModel?: IPayApplicationWithPaymentMethods;
    private nameField: MultiLanguageInput;
    private useTipSwitch: Switch;
    private billPaymentReceiptEmailCustomSubjectField: MultiLanguageInput;
    private billPaymentReceiptEmailCustomContentField: MultiLanguageInput;
    private defaultTipPercentField: NumberInput;
    private defaultTipSection: JQuery;
    private useVouchersAndDiscountsSwitch: Switch;
    private promptOperatorsForReceiptAfterPaymentSwitch: Switch;

    private cancellationEmailSection: JQuery;
    private sendCancellationEmailSwitch: Switch;
    private cancellationEmailCustomSubjectField: MultiLanguageInput;
    private cancellationEmailCustomContentField: MultiLanguageInput;

    private notificationPickUpOrderMessageTextField: MultiLanguageInput;
    private paymentMethodToggles: {
        [index: number]: {
            payNow: Switch;
            payLater: Switch;
            quickPayment: Switch | undefined;
            userSelect: SelectBootstrap<ILinkableEntity>;
        };
    } = {};

    private waiterDataSource: IDataSource<ILinkableEntity> = {
        getData(): Promise<IPaginatedResult<ILinkableEntity>> {
            return Factory.getInstance<LocationDao>(LocationDao).getOperatorsPaginated({
                locationId: TastierGlobal.getLocationSwitch()?.getCurrentLocationId()!,
            });
        },
    };

    constructor(element: JQuery) {
        super(element, Factory.getInstance<PlanningDao>(PlanningDao).payUseCaseDataSource as any);

        this.defaultTipSection = this.$form.find("#default-tip-section");

        this.nameField = MultiLanguageInput.fromInput(this.$form.find('.form-control[name="name"]'));
        this.nameField.onChange = (e) => {
            this.updateName(translate(e));
        };

        this.useTipSwitch = new Switch(this.$form.find('input[name="usetip"]'));

        this.useTipSwitch.onChange = (value) => {
            this.setDefaultTipSectionVisibility(value);
        };

        this.billPaymentReceiptEmailCustomSubjectField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="billReceiptEmailCustomSubjectField"]')
        );
        this.billPaymentReceiptEmailCustomContentField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="billReceiptEmailCustomContentField"]')
        );

        this.notificationPickUpOrderMessageTextField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="messagepickupordertext"]')
        );

        this.$form.find("#addordernumber").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.notificationPickUpOrderMessageTextField.appendText("[ordernumber]");
            return false;
        });

        this.$form.find("#bill-payment-receipt-add-location-name").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            this.billPaymentReceiptEmailCustomSubjectField.appendText("[LocationName]");
            return false;
        });

        this.$form.find("#addtablename").click((e) => {
            e.preventDefault();
            e.stopPropagation();

            return false;
        });

        this.defaultTipPercentField = new NumberInput(this.$form.find('.form-control[name="defaulttip"]'), {
            min: 0,
            max: 10,
            step: 1,
            buttondown_class: "btn btn-secondary",
            buttonup_class: "btn btn-secondary",
            verticalbuttons: true,
            verticalup: '<i class="la la-plus"></i>',
            verticaldown: '<i class="la la-minus"></i>',
        });

        this.useVouchersAndDiscountsSwitch = new Switch(this.$form.find('input[name="useVouchersAndDiscountsSwitch"]'));
        this.promptOperatorsForReceiptAfterPaymentSwitch = new Switch(
            this.$form.find('input[name="promptOperatorsForReceiptAfterPayment"]')
        );

        this.cancellationEmailSection = this.$form.find("#cancellation-email-section");

        this.sendCancellationEmailSwitch = new Switch(this.$form.find('input[name="sendCancellationEmail"]'));
        this.sendCancellationEmailSwitch.onChange = (isEnabled) => {
            this.setCancellationEmailSectionVisibility(isEnabled);
        };

        this.cancellationEmailCustomSubjectField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="cancellationEmailCustomSubjectField"]')
        );

        this.cancellationEmailCustomContentField = MultiLanguageInput.fromInput(
            this.$form.find('.form-control[name="cancellationEmailCustomContentField"]')
        );
    }

    private setDefaultTipSectionVisibility(useTip: boolean) {
        if (useTip) {
            this.defaultTipSection.show();
        } else {
            this.defaultTipSection.hide();
        }
    }

    public setFormData(model?: IPayApplicationWithPaymentMethods) {
        const useTip = model === undefined ? false : model.useTip;
        const defaultTipPercent = model === undefined || model.defaultTipPercent == null ? 5 : model.defaultTipPercent;
        const sendCancellationEmail = model === undefined ? false : model.sendCancellationEmail;

        this.originalModel = model;
        this.updateName(model === undefined ? "" : translate(model.name));
        this.nameField.setValues(model === undefined ? undefined : model.name);
        this.useTipSwitch.setValue(useTip);
        this.defaultTipPercentField.setValue(defaultTipPercent);
        this.useVouchersAndDiscountsSwitch.setValue(model?.useVouchersAndDiscounts);
        this.promptOperatorsForReceiptAfterPaymentSwitch.setValue(model?.promptOperatorsForReceiptAfterPayment);
        this.billPaymentReceiptEmailCustomSubjectField.setValues(model?.billPaymentReceiptEmailCustomSubject);
        this.billPaymentReceiptEmailCustomContentField.setValues(model?.billPaymentReceiptEmailCustomContent);

        this.sendCancellationEmailSwitch.setValue(sendCancellationEmail);
        this.cancellationEmailCustomSubjectField.setValues(
            model == null ? undefined : model.cancellationEmailCustomSubject
        );

        this.cancellationEmailCustomContentField.setValues(
            model == null ? undefined : model.cancellationEmailCustomContent
        );

        this.setDefaultTipSectionVisibility(useTip);
        this.setCancellationEmailSectionVisibility(sendCancellationEmail);

        if (model !== undefined && model.paymentMethods !== undefined && model.paymentMethods.length > 0) {
            const paymentMethodsDiv = $(".pay-application-payment-methods-group");
            paymentMethodsDiv.children().remove();

            paymentMethodsDiv.append(
                `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.payment.online")}</div>`
            );
            paymentMethodsDiv.append(
                `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
            );

            // Add rows
            const browserPaymentMethods = model.paymentMethods.filter(
                (i) => !i.isPaymentDevice && i.mode === PaymentMethodMode.Online
            );
            for (const paymentMethod of browserPaymentMethods) {
                this.insertPaymentMethodRow(paymentMethod);
            }

            const onlinePaymentDevicePaymentMethods = model.paymentMethods.filter(
                (pm) => pm.mode === PaymentMethodMode.Online && pm.isPaymentDevice
            );
            if (onlinePaymentDevicePaymentMethods.length > 0) {
                // Add section offline payment methods
                paymentMethodsDiv.append(
                    `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.payment.paymentDevices")}</div>`
                );

                // Add headers
                paymentMethodsDiv.append(
                    `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
                );

                // Add rows
                for (const paymentMethod of onlinePaymentDevicePaymentMethods) {
                    this.insertPaymentMethodRow(paymentMethod);
                }
            }

            // Add section revenue effective offline payment methods
            paymentMethodsDiv.append(
                `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.payment.offline")}</div>`
            );

            // add headers
            paymentMethodsDiv.append(
                `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
            );

            // add rows
            const offlineRevenueEffectivePaymentMethods = model.paymentMethods.filter(
                (i) => i.mode === PaymentMethodMode.Offline && i.revenueEffective
            );
            for (const paymentMethod of offlineRevenueEffectivePaymentMethods) {
                this.insertPaymentMethodRow(paymentMethod);
            }

            // Add section not revenue-effective offline payment methods
            const offlineNotRevenueEffectivePaymentMethods = model.paymentMethods.filter(
                (i) => i.mode === PaymentMethodMode.Offline && !i.revenueEffective
            );
            if (offlineNotRevenueEffectivePaymentMethods.length > 0) {
                paymentMethodsDiv.append(
                    `<hr /><div class="form-group row kt-section__title kt-section__title-sm">${i18n("useCase.notRevenueEffective")}</div>`
                );

                // add headers
                paymentMethodsDiv.append(
                    `<div class="form-group row"><div class="col-2"></div><div class="col-2">${i18n("useCase.endUsers")}</div><div class="col-2">${i18n("useCase.operators")}</div><div class="col-2">${i18n("useCase.quickButton")}</div><div class="col-2">${i18n("useCase.permission")}</div></div>`
                );
            }

            // add rows
            for (const paymentMethod of offlineNotRevenueEffectivePaymentMethods) {
                this.insertPaymentMethodRow(paymentMethod);
            }
        }
    }

    private insertPaymentMethodRow(paymentMethod: IUseCasePaymentMethod) {
        const index = this.originalModel!.paymentMethods!.indexOf(paymentMethod);
        const paymentMethodDiv = $('<div class="form-group row"></div').appendTo($(".pay-application-payment-methods-group"));
        paymentMethodDiv.append(
            `<label class="col-2 col-form-label">${translate(paymentMethod.localizedName)}</label>`
        );

        paymentMethodDiv.append(`<div class="col-2"><input type="checkbox" name="isEnabledForGuests${index}" /></div>`);
        const isEnabledForGuestsSwitch = new Switch(this.$form.find(`input[name="isEnabledForGuests${index}"]`));
        isEnabledForGuestsSwitch.setValue(paymentMethod.isEnabledForGuests);

        paymentMethodDiv.append(
            `<div class="col-2"><input type="checkbox" name="isEnabledForWaiters${index}" /></div>`
        );
        const isEnabledForWaitersSwitch = new Switch(this.$form.find(`input[name="isEnabledForWaiters${index}"]`));
        isEnabledForWaitersSwitch.setValue(paymentMethod.isEnabledForWaiters);

        const isQuickPaymentEnabledColumn = $(`<div class="col-2"></div>`).appendTo(paymentMethodDiv);
        const isQuickPaymentEnabledSection = $(`<div></div>`).appendTo(isQuickPaymentEnabledColumn);

        let isQuickPaymentEnabledSwitch: Switch | undefined = undefined;
        if (paymentMethod.isQuickPaymentAvailable) {
            $(`<input type="checkbox" name="isQuickPaymentEnabled${index}" />`).appendTo(isQuickPaymentEnabledSection);
            isQuickPaymentEnabledSwitch = new Switch(this.$form.find(`input[name="isQuickPaymentEnabled${index}"]`));
            isQuickPaymentEnabledSwitch.setValue(paymentMethod.isQuickPaymentEnabled);
        }

        const permissionSection = $('<div class="col-2"></div>').appendTo(paymentMethodDiv);
        const userSelect = new SelectBootstrap<ILinkableEntity>(
            $('<select name="user" class="form-control"></select>').appendTo(permissionSection),
            this.waiterDataSource,
            (c) => c.name,
            false,
            i18n("general.all"),
            undefined,
            true
        );

        userSelect.setValues(paymentMethod.permittedUserIds);

        this.configurePermissionSection(paymentMethod.isEnabledForWaiters, permissionSection);
        this.configureQuickPaymentSection(paymentMethod.isEnabledForWaiters, isQuickPaymentEnabledSection);

        isEnabledForWaitersSwitch.onChange = () => {
            this.configurePermissionSection(isEnabledForWaitersSwitch.getValue(), permissionSection);
            if (paymentMethod.isQuickPaymentAvailable) {
                this.configureQuickPaymentSection(isEnabledForWaitersSwitch.getValue(), isQuickPaymentEnabledSection);
            }
        };

        this.paymentMethodToggles[index] = {
            payNow: isEnabledForGuestsSwitch,
            payLater: isEnabledForWaitersSwitch,
            quickPayment: isQuickPaymentEnabledSwitch,
            userSelect: userSelect,
        };
    }

    private configurePermissionSection(isPaymentMethodEnabledForWaiters: boolean, permissionSection: JQuery) {
        if (isPaymentMethodEnabledForWaiters) {
            permissionSection.removeClass("hidden");
        } else {
            permissionSection.addClass("hidden");
        }
    }

    private configureQuickPaymentSection(
        isPaymentMethodEnabledForWaiters: boolean,
        isQuickPaymentEnabledSection: JQuery
    ) {
        if (isPaymentMethodEnabledForWaiters) {
            isQuickPaymentEnabledSection.removeClass("hidden");
        } else {
            isQuickPaymentEnabledSection.addClass("hidden");
        }
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    public getFormData($form: JQuery): ICreatePayApplicationCmd {
        // eslint-disable-next-line no-extra-boolean-cast
        const paymentMethods = !!this.originalModel
            ? this.originalModel.paymentMethods!.map((element, index) => {
                  element.isEnabledForGuests = this.paymentMethodToggles[index].payNow.getValue();
                  element.isEnabledForWaiters = this.paymentMethodToggles[index].payLater.getValue();
                  element.isQuickPaymentEnabled =
                      element.isEnabledForWaiters &&
                      this.paymentMethodToggles[index].quickPayment != null &&
                      this.paymentMethodToggles[index].quickPayment!.getValue();
                  element.permittedUserIds = this.paymentMethodToggles[index].userSelect.getValues();
                  return element;
              })
            : undefined;

        const data: ICreatePayApplicationCmd = {
            name: this.nameField.getValues(),
            useTip: this.useTipSwitch.getValue(),
            paymentMethods: paymentMethods!,
            defaultTipPercent: this.defaultTipPercentField.getValue()!,
            useVouchersAndDiscounts: this.useVouchersAndDiscountsSwitch.getValue()!,
            promptOperatorsForReceiptAfterPayment: this.promptOperatorsForReceiptAfterPaymentSwitch.getValue()!,
            billPaymentReceiptEmailCustomSubject: this.billPaymentReceiptEmailCustomSubjectField.getValues()!,
            billPaymentReceiptEmailCustomContent: this.billPaymentReceiptEmailCustomContentField.getValues()!,

            sendCancellationEmail: this.sendCancellationEmailSwitch.getValue(),
            cancellationEmailCustomContent: this.cancellationEmailCustomContentField.getValues()!,
            cancellationEmailCustomSubject: this.cancellationEmailCustomSubjectField.getValues()!,
        };

        let hasError = false;
        if (
            data.name.de === undefined &&
            data.name.en === undefined &&
            data.name.fr === undefined &&
            data.name.it === undefined
        ) {
            hasError = true;
            this.nameField.setError(true);
        } else {
            if (
                (data.name.de !== undefined && data.name.de.length > 64) ||
                (data.name.fr !== undefined && data.name.fr.length > 64) ||
                (data.name.it !== undefined && data.name.it.length > 64) ||
                (data.name.en !== undefined && data.name.en.length > 64)
            ) {
                hasError = true;
                this.nameField.setError(true);
            } else {
                this.nameField.setError(false);
            }
        }

        return (hasError ? undefined : data) as any;
    }

    protected resolveErrorMessage(e, b: DaoError): string | undefined {
        if (b?.additionalData?.reason == null) {
            return undefined;
        }

        switch (b.additionalData.reason) {
            case "entity_in_use_exception":
                let name = b.additionalData.dependingName;
                if (name == null || name === undefined)
                    name = i18n("enums.ServiceType." + ServiceType[b.additionalData.serviceType]);

                return i18n("useCase.failedDelete", { dependingName: name });
            case "too_many_quick_payment_methods_enabled_exception":
                const maxEnabledPaymentMethodsCount = b.additionalData.maxEnabledPaymentMethodsCount;
                return i18n("useCase.tooManyQuickPaymentMethodsEnabled", {
                    maxEnabledPaymentMethodsCount: maxEnabledPaymentMethodsCount,
                });
        }

        return undefined;
    }

    private setCancellationEmailSectionVisibility(showSection: boolean) {
        if (showSection) {
            this.cancellationEmailSection.removeClass("hidden");
        } else {
            this.cancellationEmailSection.addClass("hidden");
        }
    }
}
